// People lookup helpers. WeakMap-cached by root.people identity for O(1) lookups.
//
// FA204 / frank trap — _ensureMap/_fuzzyMatch/_peopleByEmailCache/
// _fuzzyByPrefixCache/_ACTIVE_ROLES/_ACTIVE_TEAM_CATEGORIES/
// _activeMembersCache used to live at module scope, referenced bare from
// every export below. Each export now carries its own self-contained inline
// copy of the pure helpers/tables. The WeakMap memoization caches are
// GENUINE cross-call state (their whole point is to survive across
// separate peopleByEmail/findPerson/activeMembers calls) with no argument-
// less escape hatch available — every caller here DOES receive/derive a
// real `root`, so each cache is re-homed onto `root.__peopleUtilsCache`
// (lazily created), the same `root.__x` idiom as navbarTabMenu.js's
// `root.__navbarSaveQueue`, instead of a fresh-per-call WeakMap (which
// would still be correct but would never hit — no different from dropping
// the cache outright).

// All functions support both direct calls (fn(root, ...)) and el.call('fn', ...) where
// root is inferred from this.state.root when omitted.
export function peopleByEmail(root) {
  function _ensureMap(root, people) {
    if (!people || !people.length) return Object.freeze({})
    let bag = root && root.__peopleUtilsCache
    if (root && !bag) bag = root.__peopleUtilsCache = {}
    let cache = bag ? bag.byEmail : null
    if (cache && typeof cache.get !== 'function') cache = null
    if (bag && !cache) cache = bag.byEmail = new WeakMap()
    let cached = cache && cache.get(people)
    if (cached) return cached
    cached = Object.create(null)
    for (const p of people) {
      if (p && p.email) cached[p.email] = p
    }
    if (cache) cache.set(people, cached)
    return cached
  }

  const r = root != null ? root : this && this.state && this.state.root
  return _ensureMap(r, (r && r.people) || null)
}

// Extract the first word of a display name. Shared by chat sidebar group
// labels, greetings on the home page, and the fuzzy-match cache below.
export const firstName = (name, fallback = '') => (name || '').split(/\s+/)[0] || name || fallback

// Resolve a person by email. Prefers the tenant-scoped `people` list
// (active-org members only) so cross-tenant lookups don't leak. Falls
// through to `peopleAll` for the caller themselves + any explicit
// lookups that need the full identity directory (e.g. findMe, calendar
// invitee resolution for events that crossed an org boundary).
export function findPerson(rootOrEmail, maybeEmail) {
  function _ensureMap(root, people) {
    if (!people || !people.length) return Object.freeze({})
    let bag = root && root.__peopleUtilsCache
    if (root && !bag) bag = root.__peopleUtilsCache = {}
    let cache = bag ? bag.byEmail : null
    if (cache && typeof cache.get !== 'function') cache = null
    if (bag && !cache) cache = bag.byEmail = new WeakMap()
    let cached = cache && cache.get(people)
    if (cached) return cached
    cached = Object.create(null)
    for (const p of people) {
      if (p && p.email) cached[p.email] = p
    }
    if (cache) cache.set(people, cached)
    return cached
  }
  // Fuzzy match by email prefix when exact lookup fails. Matches the part
  // before `@` against each person's name first-word or email prefix. Used
  // so chat messages from aliased emails (e.g. tornike@symbols.app when the
  // record is svinchy@symbols.app / Tornike Svintradze) still resolve to the
  // person's full name.
  function _fuzzyMatch(root, people, email) {
    if (!people || !people.length || !email) return null
    const at = email.indexOf('@')
    if (at < 2) return null
    const prefix = email.slice(0, at).toLowerCase()
    const domain = email.slice(at + 1).toLowerCase()
    let bag = root && root.__peopleUtilsCache
    if (root && !bag) bag = root.__peopleUtilsCache = {}
    let cache = bag ? bag.fuzzyByPrefix : null
    if (cache && typeof cache.get !== 'function') cache = null
    if (bag && !cache) cache = bag.fuzzyByPrefix = new WeakMap()
    let cached = cache && cache.get(people)
    if (!cached) {
      cached = Object.create(null)
      for (const p of people) {
        if (!p || !p.email) continue
        const pAt = p.email.indexOf('@')
        if (pAt < 0) continue
        const pPrefix = p.email.slice(0, pAt).toLowerCase()
        const pDomain = p.email.slice(pAt + 1).toLowerCase()
        cached[pPrefix + '@' + pDomain] = p
        cached[pPrefix] = p
        const first = (p.name || '').split(/\s+/)[0] || p.name || ''
        if (first) cached[first.toLowerCase()] = cached[first.toLowerCase()] || p
      }
      if (cache) cache.set(people, cached)
    }
    return cached[prefix + '@' + domain] || cached[prefix] || null
  }

  if (typeof rootOrEmail === 'string' && maybeEmail === undefined) {
    const root = this && this.state && this.state.root
    if (!root) return null
    const scoped = root.people || null
    const full = root.peopleAll || null
    return (
      _ensureMap(root, scoped)[rootOrEmail] ||
      _fuzzyMatch(root, scoped, rootOrEmail) ||
      _ensureMap(root, full)[rootOrEmail] ||
      _fuzzyMatch(root, full, rootOrEmail) ||
      null
    )
  }
  if (!rootOrEmail || !maybeEmail) return null
  const scoped = rootOrEmail.people || null
  const full = rootOrEmail.peopleAll || null
  return (
    _ensureMap(rootOrEmail, scoped)[maybeEmail] ||
    _fuzzyMatch(rootOrEmail, scoped, maybeEmail) ||
    _ensureMap(rootOrEmail, full)[maybeEmail] ||
    _fuzzyMatch(rootOrEmail, full, maybeEmail) ||
    null
  )
}

export const avatarUrl = (seed) =>
  `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(seed || '?')}`

export function avatarUrlFor(root, email) {
  // FA204 / frank trap — self-contained inline copy of findPerson/avatarUrl,
  // see the file header. Kept identical to the exports above.
  function _findPerson(rootOrEmail, maybeEmail, thisArg) {
    function _ensureMap(root, people) {
      if (!people || !people.length) return Object.freeze({})
      let bag = root && root.__peopleUtilsCache
      if (root && !bag) bag = root.__peopleUtilsCache = {}
      let cache = bag ? bag.byEmail : null
      if (cache && typeof cache.get !== 'function') cache = null
      if (bag && !cache) cache = bag.byEmail = new WeakMap()
      let cached = cache && cache.get(people)
      if (cached) return cached
      cached = Object.create(null)
      for (const p of people) {
        if (p && p.email) cached[p.email] = p
      }
      if (cache) cache.set(people, cached)
      return cached
    }
    function _fuzzyMatch(root, people, email) {
      if (!people || !people.length || !email) return null
      const at = email.indexOf('@')
      if (at < 2) return null
      const prefix = email.slice(0, at).toLowerCase()
      const domain = email.slice(at + 1).toLowerCase()
      let bag = root && root.__peopleUtilsCache
      if (root && !bag) bag = root.__peopleUtilsCache = {}
      let cache = bag ? bag.fuzzyByPrefix : null
      if (cache && typeof cache.get !== 'function') cache = null
      if (bag && !cache) cache = bag.fuzzyByPrefix = new WeakMap()
      let cached = cache && cache.get(people)
      if (!cached) {
        cached = Object.create(null)
        for (const p of people) {
          if (!p || !p.email) continue
          const pAt = p.email.indexOf('@')
          if (pAt < 0) continue
          const pPrefix = p.email.slice(0, pAt).toLowerCase()
          const pDomain = p.email.slice(pAt + 1).toLowerCase()
          cached[pPrefix + '@' + pDomain] = p
          cached[pPrefix] = p
          const first = (p.name || '').split(/\s+/)[0] || p.name || ''
          if (first) cached[first.toLowerCase()] = cached[first.toLowerCase()] || p
        }
        if (cache) cache.set(people, cached)
      }
      return cached[prefix + '@' + domain] || cached[prefix] || null
    }

    if (typeof rootOrEmail === 'string' && maybeEmail === undefined) {
      const root = thisArg && thisArg.state && thisArg.state.root
      if (!root) return null
      const scoped = root.people || null
      const full = root.peopleAll || null
      return (
        _ensureMap(root, scoped)[rootOrEmail] ||
        _fuzzyMatch(root, scoped, rootOrEmail) ||
        _ensureMap(root, full)[rootOrEmail] ||
        _fuzzyMatch(root, full, rootOrEmail) ||
        null
      )
    }
    if (!rootOrEmail || !maybeEmail) return null
    const scoped = rootOrEmail.people || null
    const full = rootOrEmail.peopleAll || null
    return (
      _ensureMap(rootOrEmail, scoped)[maybeEmail] ||
      _fuzzyMatch(rootOrEmail, scoped, maybeEmail) ||
      _ensureMap(rootOrEmail, full)[maybeEmail] ||
      _fuzzyMatch(rootOrEmail, full, maybeEmail) ||
      null
    )
  }
  function _avatarUrl(seed) {
    return `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(seed || '?')}`
  }

  let r = root,
    e = email
  if (typeof root === 'string' && email === undefined) {
    e = root
    r = this && this.state && this.state.root
  }
  const p = _findPerson(r, e, this)
  return _avatarUrl((p && p.name) || e)
}

// Active team members — people with operational roles (member, manager, admin,
// superadmin). Excludes advisors, investors, and shareholders who have left.
// CU-10 §90: roles are no longer on the person object; look up via
// root.roles.byUser[userId]. A person with no role entry defaults to 'member'
// (operational) for backward compatibility with people added before the migration.
export function activeMembers(root) {
  const ACTIVE_ROLES = new Set(['member', 'manager', 'admin', 'superadmin'])
  // Team categories that count as ACTIVE membership (Nika's 2026-06-06 model:
  // "a member is active when they belong to ≥1 active-status team"). Mirrors
  // peopleGroups.isActiveTeamCategory + team-and-agents _INACTIVE_CATEGORIES.
  const ACTIVE_TEAM_CATEGORIES = new Set(['active', 'manager'])

  const r = root != null ? root : this && this.state && this.state.root
  const people = (r && r.people) || []
  if (!people.length) return people
  const byUser = r?.roles?.byUser || {}
  // Cache key must incorporate byUser since it changes when roles load.
  // Use a two-key WeakMap trick: cache on people array, invalidate when
  // byUser reference changes via version bump (root.roles.version) OR when
  // teamsByEmail changes (a membership move doesn't bump roles.version, but
  // it DOES change active/inactive membership — see the team check below).
  const version = r?.roles?.version ?? 0
  const teamsByEmail = r?.teamsByEmail || null
  // Primary membership index — keyed on the stable non-PII userId that rides
  // every roster row. A non-mgmt viewer's rows carry NO email (server
  // memberProjection.js), so the email map can never match for them; joining
  // id-first restores team resolution platform-wide (tickets/fable.md P0
  // regression) while the email map stays as the mgmt/optimistic fallback.
  const teamsByUserId = r?.teamsByUserId || null
  let bag = r && r.__peopleUtilsCache
  if (r && !bag) bag = r.__peopleUtilsCache = {}
  let cache = bag ? bag.activeMembers : null
  if (cache && typeof cache.get !== 'function') cache = null
  if (bag && !cache) cache = bag.activeMembers = new WeakMap()
  let cached = cache && cache.get(people)
  if (
    cached &&
    cached.version === version &&
    cached.teamsByEmail === teamsByEmail &&
    cached.teamsByUserId === teamsByUserId
  ) {
    return cached.result
  }
  const result = people.filter((p) => {
    // Status check FIRST — `status: 'left'` (departed) and `'inactive'`
    // (read-only access) both drop the person from active-members lists
    // (calendar columns, sidebar PeopleSection TEAM group, chat/meet pickers).
    if (p.status === 'left' || p.status === 'inactive') return false
    // Team-based inactivity (Nika's model): when a person's team memberships
    // are loaded and NONE is an active/manager team, they're inactive —
    // e.g. only in Class B/C / external / archived teams. This is how
    // advisors/investors/shareholders get dropped from calendar columns,
    // chat/meet pickers, and the sidebar TEAM group, consistent with
    // /team-members + /team-and-agents. Skipped when no membership data has
    // loaded for them yet, to avoid over-excluding during the boot window.
    if (teamsByUserId || teamsByEmail) {
      // id-first (works for non-mgmt viewers whose rows have no email),
      // email fallback (mgmt viewers + optimistic writes keyed by email).
      const byId = teamsByUserId && p.userId != null ? teamsByUserId[String(p.userId)] : null
      const memberships =
        (Array.isArray(byId) && byId.length && byId) ||
        (teamsByEmail && teamsByEmail[String(p.email || '').toLowerCase()])
      if (
        Array.isArray(memberships) &&
        memberships.length &&
        !memberships.some((m) => m && ACTIVE_TEAM_CATEGORIES.has(m.category))
      ) {
        return false
      }
    }
    // Legacy fallback: person still has .roles from a pre-§90 render.
    if (Array.isArray(p.roles) && p.roles.length > 0) {
      return p.roles.some((role) => ACTIVE_ROLES.has(role))
    }
    const assignedRole = byUser[p.userId] || 'member'
    return ACTIVE_ROLES.has(assignedRole)
  })
  if (cache) cache.set(people, { version, teamsByEmail, teamsByUserId, result })
  return result
}

// True when the viewed person is the current user (no ?u= param or ?u= matches current user).
// Supports el.call('isSelf') and direct isSelf(s).
export function isSelf(s) {
  const state = s != null ? s : this && this.state
  const root = state && state.root
  if (!root) return true
  const viewed =
    typeof window !== 'undefined'
      ? new URLSearchParams((window.location && window.location.search) || '').get('u')
      : null
  return !viewed || viewed === root.currentUserEmail
}

export const viewedEmail = (s) => {
  const fromQuery =
    typeof window !== 'undefined'
      ? new URLSearchParams((window.location && window.location.search) || '').get('u')
      : null
  return fromQuery || (s && s.root && s.root.currentUserEmail) || ''
}

export const patchPeopleList = (list, userId, patch) =>
  (list || []).map((p) => (p.userId === userId ? { ...p, ...patch } : p))

export const viewedPerson = (s, { allowQuery = false } = {}) => {
  // FA204 / frank trap — self-contained inline copies of viewedEmail/
  // findPerson, see the file header.
  function _viewedEmail(s) {
    const fromQuery =
      typeof window !== 'undefined'
        ? new URLSearchParams((window.location && window.location.search) || '').get('u')
        : null
    return fromQuery || (s && s.root && s.root.currentUserEmail) || ''
  }
  function _findPerson(root, email) {
    function _ensureMap(root, people) {
      if (!people || !people.length) return Object.freeze({})
      let bag = root && root.__peopleUtilsCache
      if (root && !bag) bag = root.__peopleUtilsCache = {}
      let cache = bag ? bag.byEmail : null
      if (cache && typeof cache.get !== 'function') cache = null
      if (bag && !cache) cache = bag.byEmail = new WeakMap()
      let cached = cache && cache.get(people)
      if (cached) return cached
      cached = Object.create(null)
      for (const p of people) {
        if (p && p.email) cached[p.email] = p
      }
      if (cache) cache.set(people, cached)
      return cached
    }
    function _fuzzyMatch(root, people, email) {
      if (!people || !people.length || !email) return null
      const at = email.indexOf('@')
      if (at < 2) return null
      const prefix = email.slice(0, at).toLowerCase()
      const domain = email.slice(at + 1).toLowerCase()
      let bag = root && root.__peopleUtilsCache
      if (root && !bag) bag = root.__peopleUtilsCache = {}
      let cache = bag ? bag.fuzzyByPrefix : null
      if (cache && typeof cache.get !== 'function') cache = null
      if (bag && !cache) cache = bag.fuzzyByPrefix = new WeakMap()
      let cached = cache && cache.get(people)
      if (!cached) {
        cached = Object.create(null)
        for (const p of people) {
          if (!p || !p.email) continue
          const pAt = p.email.indexOf('@')
          if (pAt < 0) continue
          const pPrefix = p.email.slice(0, pAt).toLowerCase()
          const pDomain = p.email.slice(pAt + 1).toLowerCase()
          cached[pPrefix + '@' + pDomain] = p
          cached[pPrefix] = p
          const first = (p.name || '').split(/\s+/)[0] || p.name || ''
          if (first) cached[first.toLowerCase()] = cached[first.toLowerCase()] || p
        }
        if (cache) cache.set(people, cached)
      }
      return cached[prefix + '@' + domain] || cached[prefix] || null
    }
    if (!root || !email) return null
    const scoped = root.people || null
    const full = root.peopleAll || null
    return (
      _ensureMap(root, scoped)[email] ||
      _fuzzyMatch(root, scoped, email) ||
      _ensureMap(root, full)[email] ||
      _fuzzyMatch(root, full, email) ||
      null
    )
  }

  const root = s && s.root
  if (!root) return null
  const email = allowQuery ? _viewedEmail(s) : root.currentUserEmail
  return _findPerson(root, email)
}

// Profile-style pages render for `?u=email` when set; fall back to a `{}`
// placeholder so templates reading `findMe(s).name` don't crash on undefined.
// Moved from workspace/functions/index.js (2026-08-15, MemberProfile
// extraction — tickets/opus.md) so MemberProfile can live in `shared` and
// stay reachable from every host (workspace, canvas). References the two
// exports above directly (same module, not a bare module-scope helper —
// the FA204 self-contained-copy pattern this file uses elsewhere is only
// needed for helpers that AREN'T real exports).
export const findMe = (s, { allowQuery = true } = {}) => {
  return (
    viewedPerson(s, { allowQuery }) ||
    findPerson(s && s.root, s && s.root && s.root.currentUserEmail) ||
    {}
  )
}
